import { type FormEvent, useCallback, useEffect, useState } from "react";
import { authApi } from "../../lib/api/auth.js";
import { type Circle, type Level, circlesApi, meApi } from "../../lib/api/sharing.js";
import "./Sharing.css";

const LEVELS: { value: Level; label: string; hint: string }[] = [
  { value: "exact", label: "Exact", hint: "your precise location" },
  { value: "city", label: "City", hint: "your city" },
  { value: "country", label: "Country", hint: "your country only" },
  { value: "hidden", label: "Hidden", hint: "off the map" },
];

function LevelPicker({
  value,
  onPick,
  label,
}: {
  value: Level;
  onPick: (l: Level) => void;
  label: string;
}) {
  return (
    <fieldset className="shr__picker" aria-label={label}>
      {LEVELS.map((l) => (
        <button
          key={l.value}
          type="button"
          className={`shr__level${value === l.value ? " is-active" : ""}`}
          aria-pressed={value === l.value}
          onClick={() => onPick(l.value)}
        >
          {l.label}
        </button>
      ))}
    </fieldset>
  );
}

export function Sharing() {
  const [def, setDef] = useState<Level | null>(null);
  const [audience, setAudience] = useState<Record<Level, number> | null>(null);
  const [circles, setCircles] = useState<Circle[]>([]);
  const [newName, setNewName] = useState("");
  const [newLevel, setNewLevel] = useState<Level>("city");
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [meRes, audRes, circlesRes] = await Promise.all([
      authApi.me(),
      meApi.audience(),
      circlesApi.list(),
    ]);
    if (meRes.success && meRes.data) setDef(meRes.data.user.default_visibility as Level);
    if (audRes.success && audRes.data) setAudience(audRes.data.audience);
    if (circlesRes.success && circlesRes.data) setCircles(circlesRes.data.circles);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function changeDefault(level: Level) {
    let res = await meApi.setDefault(level);
    if (!res.success && res.data?.needsConfirm) {
      if (!window.confirm("This shows every un-circled connection more than before. Continue?"))
        return;
      res = await meApi.setDefault(level, true);
    }
    if (res.success) {
      setMsg(null);
      await load();
    } else setMsg(res.error ?? "Couldn't update.");
  }

  async function changeCircleLevel(circle: Circle, level: Level) {
    let res = await circlesApi.setVisibility(circle.id, level);
    if (!res.success && res.data?.needsConfirm) {
      if (!window.confirm("This widens what everyone in the circle can see. Continue?")) return;
      res = await circlesApi.setVisibility(circle.id, level, true);
    }
    if (res.success) await load();
    else setMsg(res.error ?? "Couldn't update.");
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    const res = await circlesApi.create(newName.trim(), newLevel);
    if (res.success) {
      setNewName("");
      await load();
    } else setMsg(res.error ?? "Couldn't create the circle.");
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this circle? Its people fall back to your default.")) return;
    await circlesApi.remove(id);
    await load();
  }

  return (
    <section className="shr">
      <h1 className="shr__h1">Sharing & circles</h1>
      <p className="shr__lead">You choose exactly what each person sees of you.</p>

      <div className="shr__card">
        <h2 className="shr__h2">Everyone else (default)</h2>
        <p className="shr__sub">Connections you haven't put in a circle see you at this level.</p>
        {def && <LevelPicker value={def} onPick={changeDefault} label="default visibility" />}
        {audience && (
          <p className="shr__audience">
            {audience.exact} see exact · {audience.city} city · {audience.country} country ·{" "}
            {audience.hidden} hidden
          </p>
        )}
      </div>

      <div className="shr__card">
        <h2 className="shr__h2">Your circles</h2>
        {circles.length === 0 && <p className="shr__sub">No circles yet. Make one below.</p>}
        <ul className="shr__list">
          {circles.map((circle) => (
            <li key={circle.id} className="shr__circle">
              <div className="shr__circle-head">
                <span className="shr__circle-name">{circle.name}</span>
                <button
                  type="button"
                  className="shr__del"
                  onClick={() => remove(circle.id)}
                  aria-label={`delete ${circle.name}`}
                >
                  Delete
                </button>
              </div>
              <LevelPicker
                value={circle.visibility}
                onPick={(l) => changeCircleLevel(circle, l)}
                label={`${circle.name} visibility`}
              />
            </li>
          ))}
        </ul>

        <form className="shr__create" onSubmit={create}>
          <input
            className="shr__input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New circle (e.g. Close friends)"
            aria-label="new circle name"
            maxLength={60}
          />
          <LevelPicker value={newLevel} onPick={setNewLevel} label="new circle visibility" />
          <button type="submit" className="btn btn-primary" disabled={!newName.trim()}>
            Create circle
          </button>
        </form>
      </div>

      {msg && (
        <p className="shr__msg" role="alert">
          {msg}
        </p>
      )}
    </section>
  );
}
